import { CartAPI } from '../../../api/restApi'
import { loadMissingProductDetails } from '../../../libs/productsV2'
import { AppThunk } from '../../../model/model'
import { CartProduct } from '../../../model/cart'
import { Product } from '../../../model/product'
import { dataLoadedFail } from '../../app/actions'
import { customerIdSelector, eventIdSelector, languageSelector } from '../../app/selectors'
import { tokenSelector } from '../../auth/selectors'
import { customerTypeSelector } from '../../customer/selectors'
import { setDislikeList } from '../../dislike/actions'
import { setSlowCartNotificationAction } from '../../notifications/actions'
import { loadWishlistSuccess } from '../../wishlist/actions'
import {
  cartTimestampSelector,
  cartProductsDetailsSelector,
  cartProductsSelector,
} from '../selectors/cart'
import { slice } from '../slice'
import { updateAnalytics } from './analytics'

export const getCart = (
  customerId?: string | null,
  eventId?: string | null,
  forceFullCart = false,
  reloadedCounter = 0,
): AppThunk => {
  return (dispatch, getState) => {
    dispatch(slice.actions.loadingCartSection())
    const state = getState()

    customerId = customerId || customerIdSelector(state)
    eventId = eventId || eventIdSelector(state)
    if (!customerId || !eventId) {
      return Promise.reject()
    }

    const timestamp = cartTimestampSelector(state)
    const token = tokenSelector(state)
    const lang = languageSelector(state)

    if (!token) {
      return
    }

    const cartApi = new CartAPI(token, lang)
    return cartApi
      .getFromOrigin(customerId, eventId, timestamp, forceFullCart)
      .then(response => {
        const { cart, wishlist, dislikeList } = response

        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        dispatch(setCart(cart, reloadedCounter))
        dispatch(loadWishlistSuccess(wishlist))
        dispatch(setDislikeList(dislikeList))
      })
      .catch(error => {
        return dispatch(dataLoadedFail(error))
      })
  }
}

export const loadMissingCartProductDetails = (
  missingModelCodes: string[],
  reloadedCounter = 0,
): AppThunk => (dispatch, getState) => {
  if (!missingModelCodes.length) {
    return dispatch(updateAnalytics())
  }

  dispatch(slice.actions.loadingCartSection('cartProductDetails'))

  const state = getState()

  const token = tokenSelector(state)
  const lang = languageSelector(state)
  const customerId = customerIdSelector(state)
  const eventId = eventIdSelector(state)
  const customerType = customerTypeSelector(state)

  if (missingModelCodes.length) {
    loadMissingProductDetails(
      missingModelCodes,
      { token, lang, customerId, eventId },
      false,
      'cart',
      customerType,
    )
      .then((productsChunks: any = []) => {
        const products = productsChunks.flatMap((chunk: Product) => chunk)
        return dispatch(slice.actions.saveCartProductsDetails(products))
      })
      .then(() => {
        return dispatch(updateAnalytics())
      })
      .catch(error => {
        if (reloadedCounter === 0) {
          dispatch(setSlowCartNotificationAction(true))
          return dispatch(getCart(customerId, eventId, true, reloadedCounter + 1))
        } else {
          return dispatch(dataLoadedFail(error))
        }
      })
  }
}

/**
 * @param cartData
 * @param reloadedCounter
 */
export const saveUpdatedCart = (
  cartData: { products: CartProduct[]; backOfficeNote?: string },
  reloadedCounter?: number,
): AppThunk => {
  return function saveUpdatedCartInner(dispatch, getState) {
    dispatch(slice.actions.loadCart(cartData))
    const state = getState()
    const cartProductsDetails = cartProductsDetailsSelector(state)
    const cartProducts = cartProductsSelector(state)

    const missingModelCodes = cartProducts
      .filter(({ modelCode, colorCode, upc, outOfAssortment }) => {
        return (
          !outOfAssortment &&
          (!cartProductsDetails[modelCode] ||
            !cartProductsDetails[modelCode].mocos[colorCode] ||
            !cartProductsDetails[modelCode].mocos[colorCode].sizes[upc])
        )
      })
      .map(({ modelCode }) => modelCode)

    dispatch(
      missingModelCodes.length
        ? loadMissingCartProductDetails(missingModelCodes, reloadedCounter)
        : (slice.actions.setCartProductsDetailsLoaded() as any),
    )
    dispatch(updateAnalytics())
  }
}

export const setCart = (
  cart: {
    products: CartProduct[]
    editable?: boolean
    timestamp?: number
    loaded?: boolean
    loading?: boolean
    updating?: boolean
    customerId?: string | null
    eventId?: string | null
    status?: string | null
    backOfficeNote?: string
  },
  reloadedCounter?: number,
): AppThunk => {
  return dispatch => {
    dispatch(saveUpdatedCart(cart, reloadedCounter))
    dispatch(slice.actions.setPendingApiRequestAsFullfilled())
  }
}
